import EcommerceApiInterface from "../interface/EcommerceApiInterface";
import { IApiResponse } from "../domain/responses/IApiResponse";
import { IApiPaginatedResponse } from "../domain/responses/IApiPaginatedResponse";
import { ProgressReportViewModel } from "../domain/viewmodels/report/ProgressReportViewModel";
import { IndicatorsReportViewModel } from "../domain/viewmodels/report/IndicatorsReportViewModel";
import { FinancialReportViewModel } from "../domain/viewmodels/report/FinancialReportViewModel";
import { CourseOptionViewModel } from "../domain/viewmodels/report/CourseOptionViewModel";
import { StudentOptionViewModel } from "../domain/viewmodels/report/StudentOptionViewModel";
import { ReportFilterPayload } from "../domain/payloads/progressReport/ReportFilterPayload";
import { FinancialReportFilterPayload } from "../domain/payloads/financialReport/FinancialReportFilterPayload";

export default class EcommerceManagementServices {

    public async getLastSyncStatus(institutionId: string): Promise<IApiResponse<string | null> | null> {
        return await EcommerceApiInterface
            .get(`/sync/institutions/${institutionId}/last-sync-status`)
            .then(res => {
                return res.data;
            })
            .catch(err => {
                console.log(err);
                return null;
            });
    }

    public async getProgressReport(
        institutionId: string,
        filters: ReportFilterPayload,
        page: number = 1,
        pageSize: number = 30
    ): Promise<IApiPaginatedResponse<ProgressReportViewModel[]> | null> {
        const params = new URLSearchParams();
        params.append('page', page.toString());
        params.append('pageSize', pageSize.toString());

        if (filters.courseIds) {
            filters.courseIds.forEach(id => params.append('courseIds', id));
        }
        if (filters.studentName) params.append('studentName', filters.studentName);
        if (filters.moduleStartDateFrom) params.append('moduleStartDateFrom', filters.moduleStartDateFrom);
        if (filters.moduleStartDateTo) params.append('moduleStartDateTo', filters.moduleStartDateTo);
        if (filters.moduleEndDateFrom) params.append('moduleEndDateFrom', filters.moduleEndDateFrom);
        if (filters.moduleEndDateTo) params.append('moduleEndDateTo', filters.moduleEndDateTo);
        if (filters.sortBy) params.append('sortBy', filters.sortBy);
        if (filters.sortDirection) params.append('sortDirection', filters.sortDirection);

        return await EcommerceApiInterface
            .get(`/report/institutions/${institutionId}/progress?${params.toString()}`)
            .then(res => res.data)
            .catch(err => {
                console.error(err);
                return null;
            });
    }

    public async exportProgressReport(
        institutionId: string,
        filters: ReportFilterPayload
    ): Promise<void> {
        const params = new URLSearchParams();

        if (filters.courseIds) {
            filters.courseIds.forEach(id => params.append('courseIds', id));
        }
        if (filters.studentName) params.append('studentName', filters.studentName);
        if (filters.moduleStartDateFrom) params.append('moduleStartDateFrom', filters.moduleStartDateFrom);
        if (filters.moduleStartDateTo) params.append('moduleStartDateTo', filters.moduleStartDateTo);
        if (filters.moduleEndDateFrom) params.append('moduleEndDateFrom', filters.moduleEndDateFrom);
        if (filters.moduleEndDateTo) params.append('moduleEndDateTo', filters.moduleEndDateTo);
        if (filters.sortBy) params.append('sortBy', filters.sortBy);
        if (filters.sortDirection) params.append('sortDirection', filters.sortDirection);

        const response = await EcommerceApiInterface.get(
            `/report/institutions/${institutionId}/progress/export?${params.toString()}`,
            { responseType: 'blob' }
        );

        const blob = new Blob([response.data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `relatorio-progresso-${Date.now()}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }

    public async getCourseOptions(
        institutionId: string
    ): Promise<IApiResponse<CourseOptionViewModel[]> | null> {
        return await EcommerceApiInterface
            .get(`/report/institutions/${institutionId}/courses`)
            .then(res => res.data)
            .catch(err => {
                console.error(err);
                return null;
            });
    }

    public async searchStudents(
        institutionId: string,
        name: string
    ): Promise<IApiResponse<StudentOptionViewModel[]> | null> {
        return await EcommerceApiInterface
            .get(`/report/institutions/${institutionId}/students?name=${encodeURIComponent(name)}`)
            .then(res => res.data)
            .catch(err => {
                console.error(err);
                return null;
            });
    }

    public async getIndicatorsReport(
        institutionId: string,
        filters: ReportFilterPayload,
        page: number = 1,
        pageSize: number = 30
    ): Promise<IApiPaginatedResponse<IndicatorsReportViewModel[]> | null> {
        const params = new URLSearchParams();
        params.append('page', page.toString());
        params.append('pageSize', pageSize.toString());

        if (filters.courseIds) {
            filters.courseIds.forEach(id => params.append('courseIds', id));
        }
        if (filters.studentName) params.append('studentName', filters.studentName);
        if (filters.moduleStartDateFrom) params.append('moduleStartDateFrom', filters.moduleStartDateFrom);
        if (filters.moduleStartDateTo) params.append('moduleStartDateTo', filters.moduleStartDateTo);
        if (filters.moduleEndDateFrom) params.append('moduleEndDateFrom', filters.moduleEndDateFrom);
        if (filters.moduleEndDateTo) params.append('moduleEndDateTo', filters.moduleEndDateTo);
        if (filters.sortBy) params.append('sortBy', filters.sortBy);
        if (filters.sortDirection) params.append('sortDirection', filters.sortDirection);

        return await EcommerceApiInterface
            .get(`/report/institutions/${institutionId}/indicators?${params.toString()}`)
            .then(res => res.data)
            .catch(err => {
                console.error(err);
                return null;
            });
    }

    public async getFinancialReport(
        institutionId: string,
        filters: FinancialReportFilterPayload,
        page: number = 1,
        pageSize: number = 30
    ): Promise<IApiPaginatedResponse<FinancialReportViewModel[]> | null> {
        const params = new URLSearchParams();
        params.append('page', page.toString());
        params.append('pageSize', pageSize.toString());

        if (filters.courseIds) {
            filters.courseIds.forEach(id => params.append('courseIds', id));
        }
        if (filters.studentName) params.append('studentName', filters.studentName);
        if (filters.purchaseDateFrom) params.append('purchaseDateFrom', filters.purchaseDateFrom);
        if (filters.purchaseDateTo) params.append('purchaseDateTo', filters.purchaseDateTo);
        if (filters.sortBy) params.append('sortBy', filters.sortBy);
        if (filters.sortDirection) params.append('sortDirection', filters.sortDirection);

        return await EcommerceApiInterface
            .get(`/report/institutions/${institutionId}/financial?${params.toString()}`)
            .then(res => res.data)
            .catch(err => {
                console.error(err);
                return null;
            });
    }

    public async exportFinancialReport(
        institutionId: string,
        filters: FinancialReportFilterPayload
    ): Promise<void> {
        const params = new URLSearchParams();

        if (filters.courseIds) {
            filters.courseIds.forEach(id => params.append('courseIds', id));
        }
        if (filters.studentName) params.append('studentName', filters.studentName);
        if (filters.purchaseDateFrom) params.append('purchaseDateFrom', filters.purchaseDateFrom);
        if (filters.purchaseDateTo) params.append('purchaseDateTo', filters.purchaseDateTo);
        if (filters.sortBy) params.append('sortBy', filters.sortBy);
        if (filters.sortDirection) params.append('sortDirection', filters.sortDirection);

        const response = await EcommerceApiInterface.get(
            `/report/institutions/${institutionId}/financial/export?${params.toString()}`,
            { responseType: 'blob' }
        );

        const blob = new Blob([response.data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const today = new Date().toLocaleDateString('pt-BR').replace(/\//g, '-');
        link.download = `FMC - Financeiro - ${today}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }

    public async exportIndicatorsReport(
        institutionId: string,
        filters: ReportFilterPayload
    ): Promise<void> {
        const params = new URLSearchParams();

        if (filters.courseIds) {
            filters.courseIds.forEach(id => params.append('courseIds', id));
        }
        if (filters.studentName) params.append('studentName', filters.studentName);
        if (filters.moduleStartDateFrom) params.append('moduleStartDateFrom', filters.moduleStartDateFrom);
        if (filters.moduleStartDateTo) params.append('moduleStartDateTo', filters.moduleStartDateTo);
        if (filters.moduleEndDateFrom) params.append('moduleEndDateFrom', filters.moduleEndDateFrom);
        if (filters.moduleEndDateTo) params.append('moduleEndDateTo', filters.moduleEndDateTo);
        if (filters.sortBy) params.append('sortBy', filters.sortBy);
        if (filters.sortDirection) params.append('sortDirection', filters.sortDirection);

        const response = await EcommerceApiInterface.get(
            `/report/institutions/${institutionId}/indicators/export?${params.toString()}`,
            { responseType: 'blob' }
        );

        const blob = new Blob([response.data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `relatorio-indicadores-${Date.now()}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }
}
